import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
    Chip,
    Drawer,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import { MakeOSIcon } from "@/common/CommonComponents";

const navLinks = [
    { label: "Home", url: "https://getmakeos.com/" },
    { label: "About", url: "https://getmakeos.com/#about" },
    { label: "Pricing", url: "https://getmakeos.com/#pricing" },
    { label: "Contact", url: "https://getmakeos.com/#contact" },
];

const AuthNavLink = styled.a`
    text-decoration: none;
    color: ${(props) => props.theme.palette.text.primary};
    font-weight: 500;
    font-size: 14px;
    padding: 24px 20px;
    transition:
        color 0.2s,
        transform 0.2s;
    will-change: transform, color;

    &:hover {
        color: ${(props) => props.theme.palette.primary.main};
        transform: translateY(-6px);
    }
`;

function MobileAuthNav({ theme, internalLinkLabel, handleInternalLink }) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <nav
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 20px",
                    height: "60px",
                    background: theme.palette.background.default,
                    borderBottom: `1px solid ${theme.palette.background.paper}`,
                    position: "fixed",
                    top: "0",
                    left: "0",
                    width: "100%",
                    boxSizing: "border-box",
                    zIndex: 1301, // above Drawer
                }}
            >
                <a href="https://getmakeos.com/">
                    <MakeOSIcon width={80} />
                </a>

                <IconButton>
                    {open ? (
                        <RemoveIcon onClick={() => setOpen(false)} />
                    ) : (
                        <AddIcon onClick={() => setOpen(true)} />
                    )}
                </IconButton>
            </nav>

            <Drawer
                anchor="top"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        top: "61px",
                        height: "calc(100vh - 61px)",
                    },
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        padding: "42px 12px 0 12px",
                        boxSizing: "border-box",
                        background: theme.palette.background.default,
                    }}
                >
                    <List>
                        {navLinks.map((link, index) => (
                            <ListItem key={link.label} disablePadding>
                                <ListItemButton
                                    divider
                                    component="a"
                                    href={link.url}
                                >
                                    <span
                                        style={{
                                            transform: "rotate(-90deg)",
                                            marginRight: "12px",
                                            fontSize: "14px",
                                        }}
                                    >
                                        0{index + 1}
                                    </span>
                                    <ListItemText
                                        primary={link.label}
                                        primaryTypographyProps={{
                                            fontSize: "30px",
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>

                    <Chip
                        label={internalLinkLabel}
                        variant="outlined"
                        onClick={handleInternalLink}
                        sx={{
                            position: "absolute",
                            bottom: "48px",
                            left: "12px",
                            right: "12px",
                            height: "50px",
                        }}
                    />
                </Box>
            </Drawer>
        </>
    );
}

function DesktopAuthNav({ theme, internalLinkLabel, handleInternalLink }) {
    return (
        <nav
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 60px",
                height: "80px",
                background: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.background.paper}`,
                position: "fixed",
                top: "0",
                left: "0",
                width: "100%",
                boxSizing: "border-box",
                zIndex: "10",
            }}
        >
            <a href="https://getmakeos.com/">
                <MakeOSIcon width={100} />
            </a>

            <div
                style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                }}
            >
                {navLinks.map((link) => (
                    <AuthNavLink key={link.label} href={link.url} theme={theme}>
                        {link.label}
                    </AuthNavLink>
                ))}
            </div>

            <Chip
                label={internalLinkLabel}
                variant="outlined"
                onClick={handleInternalLink}
            />
        </nav>
    );
}

export default function AuthNav({ internalLinkLabel, internalLinkUrl }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();

    const handleInternalLink = (event) => {
        event.preventDefault();
        event.stopPropagation();
        navigate(internalLinkUrl);
    };

    return isMobile ? (
        <MobileAuthNav
            theme={theme}
            internalLinkLabel={internalLinkLabel}
            handleInternalLink={handleInternalLink}
        />
    ) : (
        <DesktopAuthNav
            theme={theme}
            internalLinkLabel={internalLinkLabel}
            handleInternalLink={handleInternalLink}
        />
    );
}
